/**
 * Converte Produtos (modelo novo) nos itens que as telas do link consomem.
 * `itemLegadoDoProduto` é subconjunto de like-desktop/shared/helpers/cardapio-legado.helper.ts: manter a conversão idêntica.
 */
import { Categoria, Produto, TabelaPreco, Tamanho } from '@mobile/models/cardapio/cardapio.model';

export interface ItemLegado {
  id: string;
  codigo_interno?: string;
  name: string;
  description: string;
  type: string;
  sub_type: string;
  custom_type: string;
  price: number;
  price_broto?: number;
  price_trem?: number;
  price_bi_trem?: number;
  photo?: string;
  promo?: boolean;
  em_estoque: boolean;
  /** Só presente (false) quando produto, categoria ou tabela estão inativos. */
  ativo?: false;
}

/** Item do cardápio dinâmico: formato legado mais categoria e preço por tamanho. */
export interface ProdutoCardapio extends ItemLegado {
  categoriaId: string;
  tabelaPrecoId?: string;
  /** tamanhoId -> preço; tamanho ausente = não vendido nesse tamanho. */
  precos: Record<string, number>;
  /** tamanhoId -> promoção aplicada (`precos` já traz o preço promocional). */
  promocoes?: Record<string, PromocaoDoItem>;
}

export interface PromocaoDoItem {
  id: string;
  nome: string;
  destaque: boolean;
  precoOriginal: number;
}

interface DestinoLegado {
  type: string;
  subType: string;
  modo: 'pizza' | 'unico';
}

export function slug(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function destinoLegado(categoriaId: string): DestinoLegado | null {
  switch (categoriaId) {
    case 'pizzas-tradicionais':
      return { type: 'salgada', subType: 'Pizzas tradicionais', modo: 'pizza' };
    case 'pizzas-especiais':
      return { type: 'salgada', subType: 'Pizzas especiais', modo: 'pizza' };
    case 'pizzas-doces':
      return { type: 'doce', subType: 'option', modo: 'pizza' };
    case 'bordas':
      return { type: 'bordas', subType: 'option', modo: 'pizza' };
    case 'adicionais':
      return { type: 'adicionais', subType: 'option', modo: 'pizza' };
    case 'esfirroes-salgados':
      return { type: 'esfirrao', subType: 'salgadas', modo: 'unico' };
    case 'esfirroes-doces':
      return { type: 'esfirrao', subType: 'doces', modo: 'unico' };
    case 'bebidas':
      return { type: 'bebida', subType: 'option', modo: 'unico' };
    default:
      return null;
  }
}

/** A categoria só vale se ela e todas as ancestrais estiverem ativas. */
function categoriaAtiva(categoriaId: string, categorias: Categoria[]): boolean {
  let atual = categorias.find(c => c.id === categoriaId);
  while (atual) {
    if (!atual.ativo) return false;
    const paiId = atual.parentId;
    atual = paiId ? categorias.find(c => c.id === paiId) : undefined;
  }
  return true;
}

export function raizDaCategoria(categoria: Categoria, categorias: Categoria[]): Categoria {
  let atual = categoria;
  while (atual.parentId) {
    const paiId = atual.parentId;
    const pai = categorias.find(c => c.id === paiId);
    if (!pai) break;
    atual = pai;
  }
  return atual;
}

/** Tamanhos da raiz da categoria, ou os da categoria apontada por `tamanhosDe`. */
export function tamanhosDaCategoria(categoria: Categoria, categorias: Categoria[]): Tamanho[] {
  const raiz = raizDaCategoria(categoria, categorias);
  const dona = raiz.tamanhosDe ? categorias.find(c => c.id === raiz.tamanhosDe) ?? raiz : raiz;
  return dona.tamanhos ?? [];
}

function customTypeDaTabela(tabela: TabelaPreco | undefined, categoria: Categoria | undefined): string {
  // Tabela com o nome da categoria é a do grupo sem custom_type.
  return tabela && slug(tabela.nome) !== slug(categoria?.nome ?? '') ? tabela.nome.toUpperCase() : '';
}

function completarItem<T extends ItemLegado>(
  item: T,
  produto: Produto,
  tabela: TabelaPreco | undefined,
  categorias: Categoria[],
): T {
  if (produto.codigo_interno) item.codigo_interno = produto.codigo_interno;
  if (produto.imagens?.imagem) item.photo = produto.imagens.imagem;
  if (produto.promo) item.promo = true;
  if (produto.ativo === false || tabela?.ativo === false || !categoriaAtiva(produto.categoriaIds[0], categorias)) item.ativo = false;
  return item;
}

/** Combos e promoções não existem no legado e retornam null. */
export function itemLegadoDoProduto(produto: Produto, categorias: Categoria[], tabelas: TabelaPreco[]): ItemLegado | null {
  if (produto.tipo !== 'produto') return null;

  const categoriaId = produto.categoriaIds[0];
  const destino = destinoLegado(categoriaId);
  if (!destino) return null;

  const tabela = tabelas.find(t => t.id === produto.tabelaPrecoId);
  const categoria = categorias.find(c => c.id === categoriaId);
  const preco = (tamanho: string) => produto.precos?.[tamanho] ?? tabela?.precos[tamanho] ?? 0;

  const item: ItemLegado = {
    id: produto.id,
    name: produto.nome,
    description: produto.descricao,
    type: destino.type,
    sub_type: destino.subType,
    custom_type: customTypeDaTabela(tabela, categoria),
    price: preco(destino.modo === 'pizza' ? 'normal' : 'unico'),
    em_estoque: produto.em_estoque !== false,
  };

  if (destino.modo === 'pizza') {
    item.price_broto = preco('broto');
    item.price_trem = preco('trem');
    item.price_bi_trem = preco('bi-trem');
  }

  return completarItem(item, produto, tabela, categorias);
}

/** Qualquer categoria do modelo novo vira item; as sem equivalente legado usam a raiz como `type`. */
export function produtoCardapioDoProduto(
  produto: Produto,
  categorias: Categoria[],
  tabelas: TabelaPreco[],
): ProdutoCardapio | null {
  if (produto.tipo !== 'produto') return null;

  const categoria = categorias.find(c => c.id === produto.categoriaIds[0]);
  if (!categoria) return null;

  const destino = destinoLegado(categoria.id);
  const raiz = raizDaCategoria(categoria, categorias);
  const tabela = tabelas.find(t => t.id === produto.tabelaPrecoId);

  const precos: Record<string, number> = {};
  for (const tamanho of tamanhosDaCategoria(categoria, categorias)) {
    const valor = produto.precos?.[tamanho.id] ?? tabela?.precos[tamanho.id];
    if (valor !== undefined) precos[tamanho.id] = valor;
  }

  const item: ProdutoCardapio = {
    id: produto.id,
    name: produto.nome,
    description: produto.descricao,
    type: destino?.type ?? raiz.id,
    sub_type: destino?.subType ?? 'option',
    custom_type: customTypeDaTabela(tabela, categoria),
    price: precos.normal ?? precos.unico ?? Object.values(precos)[0] ?? 0,
    em_estoque: produto.em_estoque !== false,
    categoriaId: categoria.id,
    precos,
  };

  if (precos.broto !== undefined) item.price_broto = precos.broto;
  if (precos.trem !== undefined) item.price_trem = precos.trem;
  if (precos['bi-trem'] !== undefined) item.price_bi_trem = precos['bi-trem'];
  if (tabela) item.tabelaPrecoId = tabela.id;

  return completarItem(item, produto, tabela, categorias);
}
